import React, { useEffect, useMemo, useState } from 'react';
import { Aperture, Loader2, AlertCircle, Check, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { fetchBoardLayoutFrameCropBlobUrl, submitCertReviewCorrection } from '../services/api';

/**
 * Inline "what the AI saw vs what the AI returned" trigger for a single
 * circuit row on the CertNew post-extraction review screen.
 *
 *   ┌────────────────────────────────────────┐
 *   │ AI saw          AI said    Engineer    │
 *   │ ┌──────────┐    B 32A      C 32A       │
 *   │ │  crop    │    30 mA      30 mA       │
 *   │ │  image   │    1.5 mm²    2.5 mm²     │
 *   │ └──────────┘                           │
 *   │  [✓ Confirm]    [🎯 Save correction]   │
 *   └────────────────────────────────────────┘
 *
 * Clicking "Confirm" pushes a positive training example
 * (`status='confirmed'`); clicking "Save correction" pushes the AI ↔
 * engineer diff as a labelled negative example
 * (`status='corrected'`). After 50–100 of these the cert prompt can
 * include them as one-shot examples for measurable accuracy gains.
 */
const FLAT_KEYS = ['description', 'cableSize', 'mcbType', 'mcbRating', 'rcdRating', 'insulationResistance', 'earthLoopImpedance', 'rcdTripTime', 'polarity'];

// The five fields the backend training pipeline actually compares.
// The popover surfaces more for human eyeballs but only these are
// included in the diff judgement.
const TRAINABLE_KEYS = ['description', 'mcbType', 'mcbRating', 'rcdRating', 'bsEn'];

const labelLine = (label, ai, engineer, highlight) => {
  if (!ai && !engineer) return null;
  const differs = highlight && (String(ai || '').trim() !== String(engineer || '').trim());
  return (
    <div className="grid grid-cols-[68px,1fr,1fr] gap-2 items-baseline text-[11px] leading-tight">
      <span className="text-slate-500">{label}</span>
      <span className={`truncate ${differs ? 'text-slate-400 line-through' : 'text-slate-700'}`} title={String(ai || '—')}>
        {ai || <span className="text-slate-300">—</span>}
      </span>
      <span className={`truncate font-medium ${differs ? 'text-emerald-700' : 'text-slate-700'}`} title={String(engineer || '—')}>
        {engineer || <span className="text-slate-300">—</span>}
      </span>
    </div>
  );
};

const CertCircuitCropPopover = ({ layoutId, frameId, aiSnapshot, currentValues, disabled, label }) => {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null); // 'confirmed' | 'corrected' | null
  const [submitted, setSubmitted] = useState(null);   // 'confirmed' | 'corrected' | null

  // Has the engineer changed any of the 5 trainable flat fields since
  // the AI snapshot? Drives the button state — "Confirm" is primary
  // when nothing changed; "Save correction" is primary otherwise.
  const hasDiff = useMemo(() => {
    if (!aiSnapshot || !currentValues) return false;
    return TRAINABLE_KEYS.some(k => {
      const a = String(aiSnapshot[k] || '').trim();
      const e = String(currentValues[k] || '').trim();
      return a !== e;
    });
  }, [aiSnapshot, currentValues]);

  // Fetch the crop the first time the popover opens. Re-opening uses
  // the cached blob URL so the engineer can hop between rows without
  // hammering the cropper.
  useEffect(() => {
    if (!open || blobUrl || loading || !layoutId || !frameId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBoardLayoutFrameCropBlobUrl(layoutId, frameId)
      .then(url => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setBlobUrl(url);
      })
      .catch(e => { if (!cancelled) setError(e?.message || 'Crop preview failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, blobUrl, loading, layoutId, frameId]);

  // Revoke the blob URL on unmount. Without this, swapping certificates
  // would leak ~50–200 KB per row each time.
  useEffect(() => () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  // Reset the "submitted" badge when the engineer edits the row again,
  // so they can re-confirm or re-correct after another tweak.
  useEffect(() => {
    if (submitted) setSubmitted(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentValues)]);

  const submit = async (action) => {
    if (!layoutId || !frameId || submitting) return;
    setSubmitting(action);
    try {
      await submitCertReviewCorrection({
        layoutId, frameId, action,
        aiSnapshot: aiSnapshot || {},
        engineerValues: currentValues || {},
      });
      setSubmitted(action);
      toast.success(action === 'confirmed' ? 'Marked as correct example' : 'Saved as correction');
    } catch (e) {
      console.error('cert review submit failed', e);
      const detail = e?.response?.data?.detail || e?.message || 'Submit failed';
      toast.error(typeof detail === 'string' ? detail : 'Submit failed');
    } finally {
      setSubmitting(null);
    }
  };

  const isAvailable = layoutId && frameId && !disabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!isAvailable}
          className={`inline-flex items-center justify-center h-7 w-7 rounded transition-colors ${
            isAvailable
              ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              : 'text-slate-300 cursor-not-allowed'
          }`}
          title={isAvailable ? 'Show what the AI saw' : 'No layout frame linked to this circuit'}
          data-testid={`cert-circuit-crop-trigger-${label || frameId || 'na'}`}
        >
          <Aperture className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-3" align="start" side="right">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">
          What the AI saw {label ? `· ${label}` : ''}
        </div>
        <div className="grid grid-cols-[140px,1fr] gap-3">
          <div className="rounded border border-slate-200 bg-slate-50 flex items-center justify-center min-h-[110px]">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            ) : error ? (
              <div className="flex flex-col items-center text-center px-1">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-[10px] text-red-600 mt-1 leading-tight">{error}</span>
              </div>
            ) : blobUrl ? (
              <img
                src={blobUrl}
                alt={`Crop ${label || frameId}`}
                className="max-h-[200px] max-w-full object-contain rounded"
                data-testid={`cert-circuit-crop-image-${label || frameId}`}
              />
            ) : (
              <span className="text-[10px] text-slate-400">Loading…</span>
            )}
          </div>
          <div className="space-y-1.5 min-w-0">
            <div className="grid grid-cols-[68px,1fr,1fr] gap-2 text-[10px] uppercase tracking-wide text-slate-400">
              <span></span><span>AI returned</span><span>Engineer</span>
            </div>
            {labelLine('Description', aiSnapshot?.description, currentValues?.description, true)}
            {labelLine('Cable', aiSnapshot?.cableSize, currentValues?.cableSize, false)}
            {labelLine('MCB', [aiSnapshot?.mcbType, aiSnapshot?.mcbRating].filter(Boolean).join(' '),
              [currentValues?.mcbType, currentValues?.mcbRating].filter(Boolean).join(' '), true)}
            {labelLine('RCD', aiSnapshot?.rcdRating, currentValues?.rcdRating, true)}
            {labelLine('IR', aiSnapshot?.insulationResistance, currentValues?.insulationResistance, false)}
            {labelLine('Zs', aiSnapshot?.earthLoopImpedance, currentValues?.earthLoopImpedance, false)}
            {labelLine('Polarity', aiSnapshot?.polarity, currentValues?.polarity, false)}
            {!aiSnapshot && !currentValues && (
              <p className="text-[11px] text-slate-400 italic">No AI snapshot — this row was added or edited manually.</p>
            )}
          </div>
        </div>

        {/* Confirm / correct → cert_training_cards. Buttons gate by
            `hasDiff` so the engineer's primary CTA matches their
            actual situation: nothing changed → confirm; changed → save
            correction. After submit, both buttons get a tick badge so
            the engineer knows the signal landed. */}
        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
          {submitted ? (
            <span className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="cert-circuit-submitted-badge">
              <Check className="h-3.5 w-3.5" />
              {submitted === 'confirmed' ? 'Recorded as correct' : 'Correction saved'}
            </span>
          ) : (
            <>
              <Button
                size="sm"
                variant={hasDiff ? 'outline' : 'default'}
                disabled={!isAvailable || !!submitting}
                onClick={() => submit('confirmed')}
                className="h-8 text-[11px] gap-1"
                data-testid="cert-circuit-confirm-btn"
              >
                {submitting === 'confirmed' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Confirm
              </Button>
              <Button
                size="sm"
                variant={hasDiff ? 'default' : 'outline'}
                disabled={!isAvailable || !!submitting || !hasDiff}
                onClick={() => submit('corrected')}
                className="h-8 text-[11px] gap-1"
                title={hasDiff ? 'Save your edits as a training correction' : 'Edit the row to enable'}
                data-testid="cert-circuit-correct-btn"
              >
                {submitting === 'corrected' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />}
                Save correction
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Re-exported so tests / external consumers can also reference the
// trainable subset without needing to touch the popover internals.
export { TRAINABLE_KEYS, FLAT_KEYS };
export default CertCircuitCropPopover;
